import { Link } from "react-router-dom";
import { Heart, BadgePercent, IdCard } from "lucide-react";

const VIREA_IMG =
  "https://customer-assets.emergentagent.com/job_mobility-preview-1/artifacts/et18zhcx_ACTIVE%20VOLANTE%20HD.jpg";
const MUVIA_IMG =
  "https://customer-assets.emergentagent.com/job_mobility-preview-1/artifacts/ejchyd3u_Screenshot%202026-05-17%20113803.png";

const BADGES = [
  { icon: Heart, label: "Dispositivo Medico CE" },
  { icon: BadgePercent, label: "IVA Agevolata 4%" },
  { icon: IdCard, label: "Guida Senza Patente" },
];

// Pre-filled WhatsApp messages for each model — high-quality leads
const WA_BASE = "https://wa.me/393297452439?text=";
const waVirea = WA_BASE + encodeURIComponent(
  "Salve! Vorrei maggiori informazioni sul modello Active VIREA (versione con volante)."
);
const waMuvia = WA_BASE + encodeURIComponent(
  "Salve! Vorrei maggiori informazioni sul modello Active MUVIA (versione con manubrio)."
);

export default function GammaActive() {
  return (
    <section
      id="gamma-active"
      data-testid="gamma-active-section"
      className="relative bg-white py-20 md:py-28 overflow-hidden"
    >
      {/* Soft background accent */}
      <div
        className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full pointer-events-none opacity-50"
        style={{
          background:
            "radial-gradient(circle, rgba(90,181,0,0.08) 0%, transparent 60%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        {/* Section header */}
        <div className="text-center max-w-2xl mx-auto mb-14 md:mb-20">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#5ab500] font-bold mb-4">
            — Gamma Active
          </div>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-[#1c1f1a] leading-[1.05]">
            Due anime, una sola{" "}
            <span className="italic text-[#5ab500]">libertà</span>.
          </h2>
          <p className="mt-5 text-[#7a7f77] leading-relaxed">
            La gamma Active ti offre due modelli per ogni stile di guida:
            tradizionale o agile. Scegli la tua versione.
          </p>
        </div>

        {/* SUB-SECTION A — VIREA (image left, text right) */}
        <div
          data-testid="model-virea"
          className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center mb-20 md:mb-28"
        >
          <div className="relative order-1 lg:order-1">
            <div className="relative rounded-3xl overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.14)] group">
              <img
                src={VIREA_IMG}
                alt="Active VIREA - scooter cabinato con volante stile auto"
                className="w-full aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-[1.03]"
              />
              <div className="absolute top-5 left-5 glass-panel rounded-full px-5 py-2">
                <span className="text-[10px] uppercase tracking-[0.25em] text-[#1c1f1a] font-bold">
                  Versione · Volante
                </span>
              </div>
              <div className="absolute bottom-5 left-5 right-5 md:right-auto md:max-w-xs glass-panel rounded-2xl p-4">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#5ab500] font-bold mb-1">
                  Active VIREA
                </div>
                <div className="font-heading text-base md:text-lg font-semibold text-[#1c1f1a] leading-tight">
                  Guida familiare, controllo totale.
                </div>
              </div>
            </div>
          </div>

          <div className="order-2 lg:order-2">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#5ab500] font-bold mb-3">
              — Modello 01
            </div>
            <h3 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#1c1f1a] leading-[1.05]">
              Active <span className="italic text-[#5ab500]">VIREA</span>.
            </h3>
            <p className="mt-6 text-[#1c1f1a] leading-relaxed text-lg font-light">
              L'esperienza di un'auto.
            </p>
            <p className="mt-3 text-[#4a4f47] leading-relaxed max-w-lg">
              Dotata di volante, Virea è pensata per chi preferisce una guida
              tradizionale e familiare. Controllo totale e design ergonomico.
            </p>

            <div className="mt-8">
              <a
                href={waVirea}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-scopri-virea"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#5ab500] text-white font-bold hover:bg-[#4a9a00] hover:shadow-[0_10px_30px_rgba(90,181,0,0.45)] transition-all"
              >
                Scopri Virea
              </a>
            </div>
          </div>
        </div>

        {/* SUB-SECTION B — MUVIA (text left, image right) */}
        <div
          data-testid="model-muvia"
          className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center"
        >
          <div className="order-2 lg:order-1">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#5ab500] font-bold mb-3">
              — Modello 02
            </div>
            <h3 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#1c1f1a] leading-[1.05]">
              Active <span className="italic text-[#5ab500]">MUVIA</span>.
            </h3>
            <p className="mt-6 text-[#1c1f1a] leading-relaxed text-lg font-light">
              L'agilità dello scooter.
            </p>
            <p className="mt-3 text-[#4a4f47] leading-relaxed max-w-lg">
              Muvia offre una guida diretta tramite manubrio, ideale per chi
              cerca semplicità e massimo controllo nel traffico urbano.
            </p>

            <div className="mt-8">
              <a
                href={waMuvia}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-scopri-muvia"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#5ab500] text-white font-bold hover:bg-[#4a9a00] hover:shadow-[0_10px_30px_rgba(90,181,0,0.45)] transition-all"
              >
                Scopri Muvia
              </a>
            </div>
          </div>

          <div className="relative order-1 lg:order-2">
            <div className="relative rounded-3xl overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.14)] group">
              <img
                src={MUVIA_IMG}
                alt="Active MUVIA - scooter cabinato con manubrio guida diretta"
                className="w-full aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-[1.03]"
              />
              <div className="absolute top-5 left-5 glass-panel rounded-full px-5 py-2">
                <span className="text-[10px] uppercase tracking-[0.25em] text-[#1c1f1a] font-bold">
                  Versione · Manubrio
                </span>
              </div>
              <div className="absolute bottom-5 left-5 right-5 md:right-auto md:max-w-xs glass-panel rounded-2xl p-4">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#5ab500] font-bold mb-1">
                  Active MUVIA
                </div>
                <div className="font-heading text-base md:text-lg font-semibold text-[#1c1f1a] leading-tight">
                  Massima agilità in città.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Trust badges row */}
        <div
          data-testid="gamma-active-badges"
          className="mt-14 md:mt-20 grid grid-cols-1 sm:grid-cols-3 gap-4"
        >
          {BADGES.map(({ icon: Icon, label }) => (
            <div
              key={label}
              className="flex items-center gap-4 bg-[#f5f6f4] rounded-2xl px-5 py-4 border border-black/[0.04] hover:border-[#5ab500]/40 transition"
            >
              <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
                <Icon className="text-[#5ab500]" size={20} />
              </div>
              <div className="text-sm font-bold text-[#1c1f1a] leading-tight">
                {label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
